import { useEffect, useRef } from 'react';

import type { CallTranscriptEventCategory } from '@/signalr';
import { FaIcon } from '@/ui';
import { manageHorizontalScrolling } from '@/lib/chat/chatScrolling';
import type { ParsedTranscriptEvent } from '@ngsos/portal-core/portal';
import { formatCriticalElapsed, formatOffset } from './formatOffsets';

export type CallTranscriptEventTimelineProps = {
  events: readonly ParsedTranscriptEvent[];
  compact?: boolean;
  currentOffset?: number | null;
};

const COMPACT_SCROLL_CONTAINER_CLASS = 'call-transcript-event-timeline--auto-scroll';
const UNKNOWN_EVENT_ICON = 'fa-light fa-circle-question';

const EVENT_ICONS: Partial<Record<CallTranscriptEventCategory, string>> = {
  AddressVerified: 'fa-light fa-location-dot',
  CallbackNumberVerified: 'fa-light fa-phone',
  ChiefComplaintIdentified: 'fa-light fa-stethoscope',
  PatientAgeDetermined: 'fa-light fa-user',
  PatientGenderDetermined: 'fa-light fa-user',
  ConsciousnessLevelAlert: 'fa-light fa-eye',
  ConsciousnessLevelVerbal: 'fa-light fa-eye',
  ConsciousnessLevelPain: 'fa-light fa-eye',
  ConsciousnessLevelUnresponsive: 'fa-light fa-eye-slash',
  BreathingStatusNormal: 'fa-light fa-lungs',
  BreathingStatusAbsent: 'fa-light fa-lungs',
  AgonalBreathingDetected: 'fa-light fa-lungs',
  BreathingRestored: 'fa-light fa-lungs',
  CardiacArrestRecognized: 'fa-light fa-heart-pulse',
  HandsOnlyCprInstructed: 'fa-light fa-hand-holding-medical',
  SternumLocated: 'fa-light fa-hand-holding-medical',
  ChestCompressionsStarted: 'fa-light fa-heart-pulse',
  ChestCompressionsPaused: 'fa-light fa-heart-pulse',
  ChestCompressionsResumed: 'fa-light fa-heart-pulse',
  RescueBreathsStarted: 'fa-light fa-wind',
  RescueBreathsDelivered: 'fa-light fa-wind',
  CompressionFrequencyMonitored: 'fa-light fa-wave-pulse',
  AedAvailableCheckCompleted: 'fa-light fa-bolt',
  AedPadsApplied: 'fa-light fa-bolt',
  AedShockDelivered: 'fa-light fa-bolt',
  InfantCprInitiated: 'fa-light fa-baby',
  ChildCprInitiated: 'fa-light fa-child',
  ChokingInterventionStarted: 'fa-light fa-head-side-cough',
  HemorrhagePresenceConfirmed: 'fa-light fa-droplet',
  HemorrhageControlInitiated: 'fa-light fa-droplet',
  AirwayCleared: 'fa-light fa-wind',
  TurnedToSide: 'fa-light fa-person',
  VomitingReported: 'fa-light fa-triangle-exclamation',
  ShockPositioningInstructed: 'fa-light fa-bed-pulse',
  ChildbirthSupportStarted: 'fa-light fa-baby',
  BabyCrowningObserved: 'fa-light fa-baby',
  PostDeliveryCareStarted: 'fa-light fa-baby',
  UmbilicalCordManagementInstructed: 'fa-light fa-scissors',
  SceneSafetyConfirmed: 'fa-light fa-shield-halved',
  WeaponsAssailantCheckCompleted: 'fa-light fa-shield-halved',
  HazmatDangerCheckCompleted: 'fa-light fa-shield-halved',
  MechanismOfInjuryIdentified: 'fa-light fa-triangle-exclamation',
  PetsSecured: 'fa-light fa-paw',
  EntryAccessSecured: 'fa-light fa-door-open',
  DispatchInitiated: 'fa-light fa-truck-medical',
  ResponseModeHot: 'fa-light fa-truck-medical',
  ResponseModeCold: 'fa-light fa-truck-medical',
  ResourceLevelSetBls: 'fa-light fa-truck-medical',
  ResourceLevelSetAls: 'fa-light fa-truck-medical',
  HysteriaThresholdReached: 'fa-light fa-comments',
  RepetitivePersistenceInitiated: 'fa-light fa-comments',
  ItsNotWorkingSyndromeDetected: 'fa-light fa-comments',
  TelescopingOfTimeSyndromeDetected: 'fa-light fa-comments',
  SecondaryPatientIdentified: 'fa-light fa-users',
  ObjectionToPersistentQuestioning: 'fa-light fa-comments',
  MedicsArrived: 'fa-light fa-ambulance',
};

const CRITICAL_EVENT_CATEGORIES: ReadonlySet<CallTranscriptEventCategory> = new Set<CallTranscriptEventCategory>([
  'ConsciousnessLevelUnresponsive',
  'BreathingStatusAbsent',
  'AgonalBreathingDetected',
  'BreathingRestored',
  'CardiacArrestRecognized',
  'HandsOnlyCprInstructed',
  'SternumLocated',
  'ChestCompressionsStarted',
  'ChestCompressionsPaused',
  'ChestCompressionsResumed',
  'RescueBreathsStarted',
  'RescueBreathsDelivered',
  'CompressionFrequencyMonitored',
  'AedAvailableCheckCompleted',
  'AedPadsApplied',
  'AedShockDelivered',
  'InfantCprInitiated',
  'ChildCprInitiated',
  'ChokingInterventionStarted',
  'HemorrhagePresenceConfirmed',
  'HemorrhageControlInitiated',
  'AirwayCleared',
  'TurnedToSide',
  'VomitingReported',
  'ShockPositioningInstructed',
  'ChildbirthSupportStarted',
  'BabyCrowningObserved',
  'PostDeliveryCareStarted',
  'UmbilicalCordManagementInstructed',
  'SceneSafetyConfirmed',
  'WeaponsAssailantCheckCompleted',
  'HazmatDangerCheckCompleted',
  'MechanismOfInjuryIdentified',
  'ResponseModeHot',
  'SecondaryPatientIdentified',
]);

type TimelineVisualStyle = 'default' | 'compressions' | 'breathingSupport' | 'recovery';

function isCritical(category: CallTranscriptEventCategory): boolean {
  return CRITICAL_EVENT_CATEGORIES.has(category);
}

function getVisualStyle(category: CallTranscriptEventCategory): TimelineVisualStyle {
  switch (category) {
    case 'HandsOnlyCprInstructed':
    case 'SternumLocated':
    case 'ChestCompressionsStarted':
    case 'ChestCompressionsPaused':
    case 'ChestCompressionsResumed':
    case 'CompressionFrequencyMonitored':
    case 'InfantCprInitiated':
    case 'ChildCprInitiated':
      return 'compressions';
    case 'RescueBreathsStarted':
    case 'RescueBreathsDelivered':
    case 'AirwayCleared':
      return 'breathingSupport';
    case 'BreathingRestored':
    case 'BreathingStatusNormal':
      return 'recovery';
    default:
      return 'default';
  }
}

function getItemVisualClass(style: TimelineVisualStyle): string | null {
  switch (style) {
    case 'compressions':
      return 'call-transcript-event-timeline__item--compressions';
    case 'breathingSupport':
      return 'call-transcript-event-timeline__item--breaths';
    case 'recovery':
      return 'call-transcript-event-timeline__item--recovery';
    default:
      return null;
  }
}

function getTriggerVisualClass(style: TimelineVisualStyle): string | null {
  switch (style) {
    case 'compressions':
      return 'call-transcript-event-timeline__compact-trigger--compressions';
    case 'breathingSupport':
      return 'call-transcript-event-timeline__compact-trigger--breaths';
    case 'recovery':
      return 'call-transcript-event-timeline__compact-trigger--recovery';
    default:
      return null;
  }
}

function getIcon(category: CallTranscriptEventCategory): string {
  return EVENT_ICONS[category] ?? UNKNOWN_EVENT_ICON;
}

export function CallTranscriptEventTimeline({ events, compact = false, currentOffset = null }: CallTranscriptEventTimelineProps) {
  const orderedEvents = [...events].sort((a, b) => a.offset - b.offset);
  const orderedEventCount = orderedEvents.length;

  const scrollCleanupRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    if (compact && orderedEventCount > 0) {
      const cleanup = manageHorizontalScrolling(COMPACT_SCROLL_CONTAINER_CLASS);
      if (cleanup !== undefined) {
        scrollCleanupRef.current = cleanup;
      }
    }
  });
  useEffect(
    () => () => {
      scrollCleanupRef.current?.();
    },
    [],
  );

  return (
    <div
      className={`call-transcript-event-timeline ${compact ? `call-transcript-event-timeline--compact ${COMPACT_SCROLL_CONTAINER_CLASS}` : ''}`}
    >
      {orderedEvents.map((item, index) => {
        const critical = isCritical(item.category);
        const visualStyle = getVisualStyle(item.category);
        return (
          <div
            key={`${item.category}-${item.offset}-${index}`}
            className={`call-transcript-event-timeline__item ${critical ? 'call-transcript-event-timeline__item--critical' : 'call-transcript-event-timeline__item--routine'} ${getItemVisualClass(visualStyle) ?? ''}`}
          >
            {compact ? (
              <button
                type="button"
                className={`call-transcript-event-timeline__compact-trigger ${critical ? 'call-transcript-event-timeline__compact-trigger--critical' : 'call-transcript-event-timeline__compact-trigger--routine'} ${getTriggerVisualClass(visualStyle) ?? ''}`}
                title={item.description}
              >
                <span className="call-transcript-event-timeline__icon call-transcript-event-timeline__icon--compact">
                  <FaIcon icon={getIcon(item.category)} size="sm" />
                </span>
                <span className="call-transcript-event-timeline__compact-main">
                  <span className="call-transcript-event-timeline__title">{item.title}</span>
                  <span className="call-transcript-event-timeline__offset">
                    {critical ? formatCriticalElapsed(item.offset, currentOffset) : formatOffset(item.offset)}
                  </span>
                </span>
              </button>
            ) : (
              <>
                <div className="call-transcript-event-timeline__offset">{formatOffset(item.offset)}</div>
                <div className="call-transcript-event-timeline__content">
                  <div className="call-transcript-event-timeline__header">
                    <span className="call-transcript-event-timeline__icon">
                      <FaIcon icon={getIcon(item.category)} size="sm" />
                    </span>
                    <span className="call-transcript-event-timeline__title">{item.title}</span>
                  </div>

                  {item.description.trim() !== '' && (
                    <div className="call-transcript-event-timeline__description">{item.description}</div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
